/**
 * Scratch Card Service
 * 
 * Provides client-side API for interacting with the scratch card backend.
 * Implements the on-the-fly card generation system using database-backed storage.
 */

import axios from 'axios';
import { API_ENDPOINTS } from '../config';

class ScratchCardService {
  constructor() {
    this.apiUrl = `${API_ENDPOINTS.GAME_API}/api/scratch-cards`;
  }

  /**
   * Get all scratch card games
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of games
   */
  async getGames(options = {}) {
    try {
      const { status = 'active', limit = 10, skip = 0 } = options;
      const response = await axios.get(`${this.apiUrl}/games`, {
        params: { status, limit, skip }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting scratch card games:', error);
      throw error;
    }
  }

  /**
   * Get a scratch card game by ID
   * @param {string} gameId - The game ID
   * @returns {Promise<Object>} The game
   */
  async getGameById(gameId) {
    try {
      const response = await axios.get(`${this.apiUrl}/games/${gameId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting scratch card game ${gameId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new scratch card game
   * @param {Object} gameData - The game data
   * @returns {Promise<Object>} The created game
   */
  async createGame(gameData) {
    try {
      const response = await axios.post(`${this.apiUrl}/games`, gameData);
      return response.data;
    } catch (error) {
      console.error('Error creating scratch card game:', error);
      throw error;
    }
  }

  /**
   * Update a scratch card game
   * @param {string} gameId - The game ID
   * @param {Object} updateData - The update data
   * @returns {Promise<Object>} The updated game
   */
  async updateGame(gameId, updateData) {
    try {
      const response = await axios.put(`${this.apiUrl}/games/${gameId}`, updateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating scratch card game ${gameId}:`, error);
      throw error;
    }
  }

  /**
   * Draw a card from a game
   * @param {string} gameId - The game ID
   * @returns {Promise<Object>} The drawn card
   */
  async drawCard(gameId) {
    try {
      const response = await axios.post(`${this.apiUrl}/games/${gameId}/draw`);
      return response.data;
    } catch (error) {
      console.error(`Error drawing card from game ${gameId}:`, error);
      throw error;
    }
  }

  /**
   * Reveal a card
   * @param {string} cardId - The card ID
   * @returns {Promise<Object>} The revealed card with full details
   */
  async revealCard(cardId) {
    try {
      const response = await axios.post(`${this.apiUrl}/cards/${cardId}/reveal`);
      return response.data;
    } catch (error) {
      console.error(`Error revealing card ${cardId}:`, error);
      throw error;
    }
  }

  /**
   * Claim a prize for a winning card
   * @param {string} cardId - The card ID
   * @returns {Promise<Object>} The claimed card
   */
  async claimPrize(cardId) {
    try {
      const response = await axios.post(`${this.apiUrl}/cards/${cardId}/claim`);
      return response.data;
    } catch (error) {
      console.error(`Error claiming prize for card ${cardId}:`, error);
      throw error;
    }
  }

  /**
   * Get cards for the current user
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of cards
   */
  async getUserCards(options = {}) {
    try {
      const { status = 'all', limit = 10, skip = 0 } = options;
      const response = await axios.get(`${this.apiUrl}/cards/user`, {
        params: { status, limit, skip }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting user cards:', error);
      throw error;
    }
  }

  /**
   * Get lottery tickets for the current user
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of lottery tickets
   */
  async getUserLotteryTickets(options = {}) {
    try {
      const { limit = 50, skip = 0 } = options;
      const response = await axios.get(`${this.apiUrl}/lottery/tickets/user`, {
        params: { limit, skip }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting user lottery tickets:', error);
      throw error;
    }
  }

  /**
   * Get upcoming lottery draws
   * @param {number} limit - The number of draws to return
   * @returns {Promise<Array>} Array of upcoming draws
   */
  async getUpcomingDraws(limit = 5) {
    try {
      const response = await axios.get(`${this.apiUrl}/lottery/draws/upcoming`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting upcoming lottery draws:', error);
      throw error;
    }
  }

  /**
   * Get card statistics for a game
   * @param {string} gameId - The game ID
   * @returns {Promise<Object>} Card statistics
   */
  async getCardStatistics(gameId) {
    try {
      const response = await axios.get(`${this.apiUrl}/games/${gameId}/stats`);
      return response.data;
    } catch (error) {
      console.error(`Error getting card statistics for game ${gameId}:`, error);
      throw error;
    }
  }


  // End of service methods
}

export default new ScratchCardService();
